
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MockAPI } from '../services/api';
import { compressImage } from '../services/imageUtils';
import { Asset, AssetStatus, AssetCondition, Document, RecordLink, DocumentSource } from '../types';
import { Card, Button } from '../components/ui';
import { ArrowLeft, Save, Trash2, AlertCircle, Upload, FileText, Image as ImageIcon, Camera } from 'lucide-react';

const AssetForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState<Partial<Asset>>({
    name: '',
    brand: '',
    status: AssetStatus.Active,
    condition_at_purchase: AssetCondition.New,
    purchase_price: 0
  });
  const [error, setError] = useState<string | null>(null);

  // Document State
  const [file, setFile] = useState<File | null>(null);
  const [fileCaption, setFileCaption] = useState<string>('');
  const [existingDocuments, setExistingDocuments] = useState<Document[]>([]);

  useEffect(() => {
    const load = async () => {
      if (isEditMode && id) {
        const asset = await MockAPI.getAsset(id);
        if (asset) {
          setFormData(asset);
          setExistingDocuments(await MockAPI.getDocumentsForEntity('asset', asset.asset_id));
        } else {
          setError("Asset not found");
        }
      }
    };
    load();
  }, [isEditMode, id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
          setFile(e.target.files[0]);
      }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.brand) {
      setError("Name and Brand are required");
      return;
    }

    try {
      // 1. Save Asset
      const savedAsset = await MockAPI.saveAsset(formData as Asset);

      // 2. Handle File Upload
      if (file) {
          const fileType = file.name.split('.').pop()?.toLowerCase() || 'unknown';
          const base64 = await compressImage(file);

          const newDoc: Document = {
              document_id: '',
              title: file.name,
              file_path: `/uploads/${file.name}`,
              file_base64: base64,
              file_type: fileType,
              source: DocumentSource.Uploaded,
              uploaded_date: new Date().toISOString(),
              is_confidential: false,
              notes: fileCaption || `Asset Record: ${savedAsset.name}`,
              server_version: 0
          };
          const savedDoc = await MockAPI.saveDocument(newDoc);
          
          const newLink: RecordLink = {
              record_link_id: '0',
              from_record_type: 'asset', from_record_id: savedAsset.asset_id,
              to_record_type: 'document', to_record_id: savedDoc.document_id,
              entity_type: 'asset',
              entity_id: savedAsset.asset_id,
              document_id: savedDoc.document_id,
              linked_date: new Date().toISOString(),
              linked_by: 'User',
              server_version: 0
          };
          await MockAPI.saveRecordLink(newLink);
      }

      navigate(`/assets/${savedAsset.asset_id}`);
    } catch (err: any) {
      setError(err.message || "Failed to save asset");
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !id) return;
    if (window.confirm("Are you sure you want to delete this asset?")) {
      await MockAPI.deleteAsset(id);
      navigate('/assets');
    }
  };

  const isImage = (type?: string) => {
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(type?.toLowerCase() || '');
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="outline" onClick={() => navigate(-1)} className="p-2">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Asset' : 'Add New Asset'}
          </h1>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Card className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700">Product Name *</label>
                    <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                        placeholder="e.g. Utility Tractor"
                    />
                </div>
                 <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700">Brand / Make *</label>
                    <input
                        type="text"
                        name="brand"
                        required
                        value={formData.brand}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                        placeholder="e.g. John Deere"
                    />
                </div>
                
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Model Year</label>
                    <input
                        type="number"
                        name="model_year"
                        value={formData.model_year || ''}
                        onChange={handleNumberChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    />
                </div>

                 <div>
                    <label className="block text-sm font-medium text-gray-700">Serial Number</label>
                    <input
                        type="text"
                        name="serial_number"
                        value={formData.serial_number || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    />
                </div>

                <div className="col-span-2 border-t border-gray-100 my-2"></div>

                 <div>
                    <label className="block text-sm font-medium text-gray-700">Purchase Date</label>
                    <input
                        type="date"
                        name="purchase_date"
                        value={formData.purchase_date || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    />
                </div>

                 <div>
                    <label className="block text-sm font-medium text-gray-700">Purchase Price ($)</label>
                    <input
                        type="number"
                        step="0.01"
                        name="purchase_price"
                        value={formData.purchase_price}
                        onChange={handleNumberChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Condition at Purchase</label>
                    <select
                        name="condition_at_purchase"
                        value={formData.condition_at_purchase}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    >
                        {Object.values(AssetCondition).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                 <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    >
                         {Object.values(AssetStatus).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                 
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Warranty Expiration</label>
                    <input
                        type="date"
                        name="warranty_expiration"
                        value={formData.warranty_expiration || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    />
                </div>

                <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Description / Specs</label>
                    <textarea
                        name="description"
                        rows={3}
                        value={formData.description || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    />
                </div>

                 <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Private Notes</label>
                    <textarea
                        name="notes"
                        rows={2}
                        value={formData.notes || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    />
                </div>
                
                {/* Documents & Photos Section */}
                <div className="col-span-2 pt-4 border-t border-gray-100">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Documents & Photos</label>
                    
                    {/* Existing Files List */}
                    {isEditMode && existingDocuments.length > 0 && (
                        <div className="mb-4 space-y-2">
                            {existingDocuments.map(doc => (
                                <div key={doc.document_id} className="flex items-center text-sm text-gray-600 bg-gray-50 p-3 rounded border border-gray-200">
                                    {isImage(doc.file_type) ? (
                                        <ImageIcon className="h-4 w-4 mr-3 text-purple-500" />
                                    ) : (
                                        <FileText className="h-4 w-4 mr-3 text-blue-500" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 truncate">{doc.title}</div>
                                        {doc.notes && <div className="text-xs text-gray-500">{doc.notes}</div>}
                                    </div>
                                    <span className="text-xs text-gray-400 ml-2">{doc.uploaded_date.split('T')[0]}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Upload New */}
                    <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                        <div className="text-sm font-medium text-gray-700 mb-2">Upload Receipt, Warranty, or Photo</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <input 
                                    type="text"
                                    placeholder="Caption (e.g. 'Purchase Receipt')"
                                    value={fileCaption}
                                    onChange={(e) => setFileCaption(e.target.value)}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border mb-2"
                                />
                                <input 
                                    type="file" 
                                    accept="image/*,application/pdf"
                                    onChange={handleFileChange}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-usda-50 file:text-usda-700 hover:file:bg-usda-100"
                                />
                                <p className="mt-1 text-xs text-gray-500 flex items-center">
                                    <Camera className="h-3 w-3 mr-1" /> Use your camera to snap a photo, or upload a file.
                                </p>
                            </div>
                        </div>
                        {file && (
                             <p className="mt-2 text-sm text-green-600 font-medium">Ready to upload: {file.name}</p>
                        )}
                    </div>
                </div>

            </div>

            <div className="pt-4 border-t border-gray-100 flex justify-between">
                {isEditMode ? (
                     <Button type="button" variant="danger" onClick={handleDelete}>
                        <Trash2 className="h-4 w-4 mr-2" /> Delete Asset
                    </Button>
                ) : (
                    <div></div>
                )}
                <Button type="submit">
                    <Save className="h-4 w-4 mr-2" /> Save Asset
                </Button>
            </div>
        </Card>
      </form>
    </div>
  );
};

export default AssetForm;
